import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicationEnum } from '../../enums/application.enum';
import { Globals } from '../../globals';
import { filterOutputs, filterOutputsOnDevices } from '../../helpers';
import { Configuration } from '../configurations/configurations.interfaces';
import { NetworkDevice } from '../networkdevices/networkdevices.interfaces';
import { PaymentResult } from '../products/payment-result.interface';
import { User } from '../users/users.interfaces';
import { CanAddDeviceResultDto } from './can-add-device-result-dto.interface';
import {
  CanAddApplicationToDeviceMetadata,
  Device,
  IpCamera,
  IpCameraConfiguration,
  ProductConfiguration,
  Satel,
  SatelConfiguration,
  UbieScenesResponse,
} from './devices.interfaces';

@Injectable({
  providedIn: 'root',
})

/**
 * Devices REST calls
 */
export class DevicesService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly platform = inject(Platform);

  private readonly endpoint = `${this.globals.apiRoot}/jwtdevices`;

  /**
   * get all devices for the currently logged in user
   */
  getDevices(): Observable<Device[]> {
    const url = `${this.endpoint}`;
    return this.http.get<Device[]>(url);
  }

  /**
   * get all luqas devices for the currently logged in user and exclude the products that are linked to a product with the provided id
   * @productId id of the product
   */
  getLuqasDevices__excludeLinkedToProductWithId(
    productId: number,
  ): Observable<NetworkDevice[]> {
    return this.http.get<Device[]>(
      `${this.globals.apiRoot}/jwtDevices/luqas/${productId}`,
    );
  }

  /**
   * get a count of devices for the currently logged in user
   */
  getDevicesCount(): Observable<number> {
    const url = `${this.endpoint}/count`;
    return this.http.get<number>(url);
  }

  /**
   * Get device by id
   * @param id Device id
   */
  getDevice(id: number): Observable<Device> {
    const url = `${this.endpoint}/${id}`;
    return this.http.get<Device>(url);
  }

  /**
   * Get device by id without installer link
   * @param id Device id
   */
  getDeviceById(id: number): Observable<Device> {
    const url = `${this.endpoint}/byId/${id}`;
    return this.http.get<Device>(url);
  }

  /**
   * Get device for timezone
   * @returns
   */
  getDevicesTimezones(): Observable<Device[]> {
    const url = `${this.endpoint}/timezones`;
    return this.http.get<Device[]>(url);
  }

  /**
   * Add a device
   * @param device the device to add
   */
  addDevice(device: Device): Observable<Device> {
    const url = `${this.endpoint}`;
    return this.http.post<Device>(url, device);
  }

  /**
   * can add a device
   * @param device the device to add
   */
  canAddDevice(device: Device): Observable<CanAddDeviceResultDto> {
    const url = `${this.endpoint}/${device.serialNumber}/${device.activationKey}/canadd`;
    return this.http.get<CanAddDeviceResultDto>(url);
  }

  /**
   * update a device
   * @param device The device you want to update with the properties as you need them to be
   */
  updateDevice(device: Device): Observable<void> {
    const url = `${this.endpoint}/${device.id}`;
    return this.http.put<void>(url, device);
  }

  /**
   * Remove a device
   * @param deviceId the device id
   * @returns return a boolean if it succeeded or not
   */
  deleteDevice(deviceId: number): Observable<void> {
    const url = `${this.endpoint}/${deviceId}`;
    return this.http.delete<void>(url);
  }

  /*************** CONFIGURATIONS *****************/

  /**
   * Get all configurations for a device
   * @param id Device id
   */
  getConfigurationsForDevice(id: number): Observable<Configuration[]> {
    const url = `${this.endpoint}/${id}/configurations`;
    return this.http.get<Configuration[]>(url);
  }

  /******************* NetworkDevices **************************/
  /**
   * get all devices and network devices for the currently logged in user
   */
  getAllDevicesWithNetworkDevices(): Observable<Device[]> {
    const url = `${this.endpoint}/networkdevices`;
    return this.http.get<Device[]>(url);
  }

  /******************* Outputs **************************/

  /**
   * get all device details and outputs for the Device id
   * DEPRECATED - use getDeviceWithFunctionblocks
   */
  getDeviceWithOutputs(id: number): Observable<Device> {
    const url = `${this.endpoint}/${id}/outputs`;
    return this.http.get<Device>(url).pipe(map(filterOutputs));
  }

  /**
   * get all device details and functionblocks for by device id
   * @param id device id
   */
  getDeviceWithFunctionblocks(id: number): Observable<Device> {
    const url = `${this.endpoint}/${id}/functionblocks`;
    return this.http.get<Device>(url).pipe(map(filterOutputs));
  }

  /**
   * Get all devices and network devices for the currently logged-in user.
   * Optionally include subscriptions and logic controllers.
   */
  getAllDevicesWithNetworkDevicesAndFunctionblocks(
    shouldIncludeSubs = false,
    shouldIncludeLogicDevices = false,
  ): Observable<Device[]> {
    const params = new HttpParams()
      .set('includeSubs', shouldIncludeSubs.toString())
      .set('includeLogicDevices', shouldIncludeLogicDevices.toString());

    return this.http.get<Device[]>(
      `${this.endpoint}/networkdevices/functionblocks`,
      { params },
    );
  }

  /**
   * get all devices and network devices for the currently logged in user
   */
  getAllDevicesWithNetworkDevicesAndScenes(): Observable<Device[]> {
    const url = `${this.endpoint}/networkdevices/scenes`;
    return this.http.get<Device[]>(url);
  }

  /**
   * get all scenes for the currently logged in user
   */
  getAllDevicesWithNetworkDevicesAndScenesV2(): Observable<UbieScenesResponse> {
    const url = `${this.globals.apiRoot}/jwtdevices/networkdevices/scenesv2`;
    return this.http.get<UbieScenesResponse>(url);
  }

  /**
   * get all devices and network devices for the currently logged in user
   */
  getAllUbieDevices(): Observable<Device[]> {
    const url = `${this.endpoint}/ubies`;
    return this.http.get<Device[]>(url);
  }

  /**
   * get all devices, network devices, functionblocks and their link to the group for the currently logged in user
   * @param id group id
   */
  getGroupDevicesWithNetworkDevicesAndFunctionblocks(
    groupId: number,
  ): Observable<Device[]> {
    const url = `${this.endpoint}/networkdevices/functionblocks/group/${groupId}`;
    return this.http.get<Device[]>(url).pipe(map(filterOutputsOnDevices));
  }

  /**
   * get all devices, network devices, functionblocks  for the currently logged in user and the installation given
   * @param id group id
   */
  getAllDevicesWithNetworkDevicesAndFunctionblocksByInstallationId(
    installationId: number,
    isShared = false,
  ): Observable<Device[]> {
    const url = `${this.endpoint}/networkdevices/functionblocks/installation/${installationId}?isShared=${isShared}`;
    return this.http.get<Device[]>(url).pipe(map(filterOutputsOnDevices));
  }

  getIfHasUbieDevic(): Observable<boolean> {
    const url = `${this.endpoint}/hasubie`;
    return this.http.get<boolean>(url);
  }

  getDevicesByProductUid(uid: string): Observable<string[]> {
    const url = `${this.globals.apiRoot}/jwtdevices/ByProductUid/${uid}`;

    return this.http
      .get<Device[]>(url)
      .pipe(map((x) => x.map((device) => device.serialNumber)));
  }

  /******************* Users **************************/

  getInstallerOfDevice(id: number): Observable<User> {
    const url = `${this.endpoint}/${id}/installer`;
    return this.http.get<User>(url);
  }

  canAddApplicationToDevice(
    deviceId: number,
    applicationName: string,
  ): Observable<CanAddApplicationToDeviceMetadata> {
    const url = `${this.endpoint}/${deviceId}/applications/${applicationName}/can-add`;
    return this.http.get<CanAddApplicationToDeviceMetadata>(url);
  }

  isApplicationActivated(
    deviceId: number,
    applicationName: string,
  ): Observable<boolean> {
    const url = `${this.endpoint}/${deviceId}/applications/${applicationName}/is-activated`;
    return this.http.get<boolean>(url);
  }

  activateArtsoundSmartFamilyApplication(deviceId: number): Observable<void> {
    const url = `${this.endpoint}/activatesmarthydedevice/${deviceId}`;
    return this.http.post<void>(url, null);
  }

  activateSonos(deviceId: number, code: string): Observable<void> {
    const url = `${this.endpoint}/activatesonos`;
    return this.http.post<void>(url, { deviceId, code });
  }

  activateDenon(
    deviceId: number,
    username: string,
    password: string,
  ): Observable<void> {
    const url = `${this.endpoint}/activatedenon`;
    return this.http.post<void>(url, { deviceId, username, password });
  }

  addIpCamera(deviceId: number, ipCamera: IpCamera): Observable<void> {
    const url = `${this.endpoint}/${deviceId}/cameras`;
    return this.http.post<void>(url, ipCamera);
  }

  addSatel(deviceId: number, satel: Satel): Observable<void> {
    const url = `${this.endpoint}/${deviceId}/satel`;
    return this.http.post<void>(url, satel);
  }

  activateController(
    ubieSerial: string,
    networkDeviceSerial: string,
    networkDeviceId: number,
    authKey: string,
  ): Observable<string> {
    const url = `${this.endpoint}/activatecontroller`;
    return this.http.post<string>(url, {
      ubieSerial,
      networkDeviceSerial,
      networkDeviceId,
      authKey,
    });
  }

  getDeviceDenonConfiguration(
    deviceId: number,
  ): Observable<ProductConfiguration> {
    const url = `${this.endpoint}/${deviceId}/configuration/denon`;
    return this.http.get<ProductConfiguration>(url);
  }

  getDeviceIpCameraConfiguration(
    deviceId: number,
  ): Observable<IpCameraConfiguration> {
    const url = `${this.endpoint}/${deviceId}/configuration/ip-camera`;
    return this.http.get<IpCameraConfiguration>(url);
  }

  getDeviceSatelConfiguration(
    deviceId: number,
  ): Observable<SatelConfiguration> {
    const url = `${this.endpoint}/${deviceId}/configuration/satel`;
    return this.http.get<SatelConfiguration>(url);
  }

  updateDeviceDenonConfiguration(
    deviceId: number,
    configuration: ProductConfiguration,
  ): Observable<void> {
    const url = `${this.endpoint}/${deviceId}/configuration/denon`;
    return this.http.put<void>(url, configuration);
  }

  updateDeviceIpCameraConfiguration(
    deviceId: number,
    configuration: IpCameraConfiguration,
  ): Observable<void> {
    const url = `${this.endpoint}/${deviceId}/configuration/ip-camera`;
    return this.http.put<void>(url, configuration);
  }

  updateDeviceSatelConfiguration(
    deviceId: number,
    configuration: SatelConfiguration,
  ): Observable<void> {
    const url = `${this.endpoint}/${deviceId}/configuration/satel`;
    return this.http.put<void>(url, configuration);
  }

  getPaymentUrl(
    deviceId: number,
    applicationName: ApplicationEnum,
  ): Observable<PaymentResult> {
    let platformName = 'browser';
    if (this.platform.is('android')) platformName = 'android';
    else if (this.platform.is('ios')) platformName = 'ios';
    const url = `${this.endpoint}/${deviceId}/products/${applicationName}/pay?platform=${platformName}`;
    return this.http.get<PaymentResult>(url);
  }
}
